import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import DataTable from "react-data-table-component";
import Swal from "sweetalert2";
import {
  AlertCircle,
  CheckCircle2,
  Database,
  DownloadCloud,
  Eye,
  FileCheck2,
  FileText,
  Hash,
  Info,
  LoaderCircle,
  LockKeyhole,
  Pencil,
  ReceiptIndianRupee,
  RefreshCw,
  Save,
  Search,
  Settings2,
  ShieldCheck,
  Trash2,
  UploadCloud,
  X,
} from "lucide-react";

import { AuthContext } from "../AuthContext";
import axiosInstance from "../axiosConfig";
import "../assets/CSS/Dashboard.css";
import Header from "../components/Header";
import PurchaseValidationModeSwitch from "../components/PurchaseValidationModeSwitch";
import Sidebar from "../components/Sidebar";
import "./PurchaseVoucherInvoice.css";

const EMPTY_STATS = { Total: 0, NotVerified: 0, Verified: 0, Approved: 0 };
const EMPTY_FORM = {
  voucherNumber: "",
};
const VOUCHER_SWAL_LAYER = { container: "voucher-swal-layer" };

const STATUS_META = {
  RED: {
    label: "Not Verified",
    helper: "Check the specific issue",
    icon: AlertCircle,
  },
  GREEN: {
    label: "Verified",
    helper: "Voucher and amount matched",
    icon: CheckCircle2,
  },
  APPROVED: {
    label: "Approved",
    helper: "Matched automatically and locked",
    icon: LockKeyhole,
  },
};

function money(value) {
  if (value === null || value === undefined || value === "") return "—";
  const number = Number(value);
  if (!Number.isFinite(number)) return "—";
  return number.toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function sameMoney(left, right) {
  if (left === null || left === undefined || left === "" || right === null || right === undefined || right === "") {
    return false;
  }
  const leftNumber = Number(left);
  const rightNumber = Number(right);
  return Number.isFinite(leftNumber)
    && Number.isFinite(rightNumber)
    && Math.round(leftNumber * 100) === Math.round(rightNumber * 100);
}

function dateOnly(value) {
  if (!value) return "—";
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : "—";
}

function dateTime(value) {
  if (!value) return "—";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? "—"
    : parsed.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

function errorMessage(error, fallback) {
  return error?.response?.data?.message || error?.message || fallback;
}

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.RED;
  const Icon = meta.icon;
  return (
    <span className={`voucher-status voucher-status--${String(status || "RED").toLowerCase()}`}>
      <Icon size={13} />
      {meta.label}
    </span>
  );
}

function MetricCard({ label, value, detail, tone, icon: Icon }) {
  return (
    <article className={`voucher-metric voucher-metric--${tone}`}>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{detail}</small>
      </div>
      <span className="voucher-metric__icon"><Icon size={22} /></span>
    </article>
  );
}

function Field({ label, value, accent = false, mono = false }) {
  return (
    <div className={`voucher-detail-field ${accent ? "is-accent" : ""}`}>
      <span>{label}</span>
      <strong className={mono ? "voucher-mono" : ""}>{value || "—"}</strong>
    </div>
  );
}

function reviewCopy(record) {
  if (!record) return { title: "Loading verification", detail: "Please wait…", kind: "LOADING" };
  if (record.Status === "APPROVED") {
    return { title: "Reviewed, approved and locked", detail: "This record cannot be edited or deleted.", kind: "APPROVED" };
  }
  if (record.Status === "GREEN") {
    return { title: "Legacy verified record", detail: "Refresh after the automatic-approval migration is applied.", kind: "MATCHED" };
  }

  const reasons = (record.MismatchReasons || []).map((reason) => String(reason).toLowerCase());
  if (reasons.some((reason) => reason.includes("amount mismatch"))) {
    return {
      title: "Amount needs review",
      detail: `Voucher ${record.VoucherNumber || "number"} was found, but the bill and ExpenseEntries amounts are different.`,
      kind: "AMOUNT_MISMATCH",
    };
  }
  if (reasons.some((reason) => reason.includes("voucher number was not detected"))) {
    return { title: "Voucher number needs review", detail: "The handwritten VN was not detected on the bill.", kind: "VOUCHER_MISSING" };
  }
  if (reasons.some((reason) => reason.includes("was not found in expenseentries"))) {
    return { title: "Voucher number not found", detail: "The detected VN does not exist in ExpenseEntries.", kind: "VOUCHER_NOT_FOUND" };
  }
  if (reasons.some((reason) => reason.includes("grand total"))) {
    return { title: "Bill amount needs review", detail: "The final grand total was not detected or is invalid.", kind: "AMOUNT_MISSING" };
  }
  if (reasons.some((reason) => reason.includes("multiple expenseentries"))) {
    return { title: "Multiple matching entries need review", detail: "More than one ExpenseEntries row has the same Voucher Number and amount.", kind: "MULTIPLE" };
  }
  return { title: "Verification needs review", detail: "Read the specific reason below and correct only the affected value.", kind: "OTHER" };
}

function expenseMatchLabel(record) {
  if (record?.ExpenseEntryID) return `Expense #${record.ExpenseEntryID}`;
  const reasons = (record?.MismatchReasons || []).map((reason) => String(reason).toLowerCase());
  if (reasons.some((reason) => reason.includes("amount mismatch"))) return "Voucher found · amount mismatch";
  if (reasons.some((reason) => reason.includes("voucher number was not detected"))) return "Voucher number missing";
  if (reasons.some((reason) => reason.includes("was not found in expenseentries"))) return "Voucher not found";
  return "No unique Expense match";
}

export default function PurchaseVoucherInvoice() {
  const { user } = useContext(AuthContext);
  const fileInputRef = useRef(null);
  const [isSidebarVisible, setIsSidebarVisible] = useState(
    () => typeof window === "undefined" || window.innerWidth >= 992
  );
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(EMPTY_STATS);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [activeStatus, setActiveStatus] = useState("ALL");
  const [search, setSearch] = useState("");

  const [showUpload, setShowUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [showLookup, setShowLookup] = useState(false);
  const [lookupVoucher, setLookupVoucher] = useState("");
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);

  const [showDetails, setShowDetails] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [correctionLookup, setCorrectionLookup] = useState(null);
  const [correctionLookupLoading, setCorrectionLookupLoading] = useState(false);
  const [actionId, setActionId] = useState(null);
  const [selectedExportRows, setSelectedExportRows] = useState([]);
  const [exportingFormat, setExportingFormat] = useState(null);

  const [showPrompt, setShowPrompt] = useState(false);
  const [promptText, setPromptText] = useState("");
  const [promptLoading, setPromptLoading] = useState(false);
  const [promptSaving, setPromptSaving] = useState(false);

  const normalizedRole = String(user?.Auth || "").trim().toLowerCase().replace(/\s+/g, "");
  const isAdmin = ["admin", "superadmin", "owner"].includes(normalizedRole);

  const loadBills = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    setLoadError("");
    try {
      const response = await axiosInstance.get("/api/purchase/voucher/bill/list");
      setRows(Array.isArray(response.data?.data) ? response.data.data : []);
      setStats({ ...EMPTY_STATS, ...(response.data?.stats || {}) });
    } catch (error) {
      setLoadError(errorMessage(error, "Voucher bills could not be loaded."));
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBills();
  }, [loadBills]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (activeStatus !== "ALL" && row.Status !== activeStatus) return false;
      if (!term) return true;
      return [row.VoucherNumber, row.PartyName, row.InvoiceNumber, row.ExpenseLedgerName, row.ID]
        .some((value) => String(value || "").toLowerCase().includes(term));
    });
  }, [rows, activeStatus, search]);

  const selectedExportIds = useMemo(
    () => selectedExportRows.filter((row) => row.Status === "APPROVED").map((row) => row.ID),
    [selectedExportRows]
  );

  const selectedReview = reviewCopy(selected);
  const selectedExpense = selected?.ExpenseEntryID
    ? {
        id: selected.ExpenseEntryID,
        voucherNumber: selected.CurrentExpenseVoucher || selected.VoucherNumber,
        amount: selected.ExpenseAmount,
        ledgerName: selected.ExpenseLedgerName,
        ledgerCode: selected.ExpenseLedgerCode,
        expenseDate: selected.ExpenseDate,
        budgetHead: selected.ExpenseBudgetHead,
        employeeName: selected.ExpenseEmployeeName,
        details: selected.ExpenseDetails,
      }
    : selected?.ExpenseCandidates?.length === 1
      ? selected.ExpenseCandidates[0]
      : null;
  const selectedAmountDifference = selectedExpense && Number.isFinite(Number(selected?.OCRAmount)) && Number.isFinite(Number(selectedExpense.amount))
    ? Math.abs(Number(selectedExpense.amount) - Number(selected.OCRAmount))
    : null;
  const correctionMatches = correctionLookup?.matches || [];
  const correctionExactMatches = correctionMatches.filter((entry) => sameMoney(entry.amount, selected?.OCRAmount));
  const correctionHasExactMatch = correctionExactMatches.length === 1;
  const correctionIsManualOverride = correctionLookup?.found
    && correctionExactMatches.length === 0
    && correctionMatches.length === 1;
  const correctionCanConfirm = correctionHasExactMatch || correctionIsManualOverride;

  const chooseFile = (file) => {
    if (!file) return;
    const extension = file.name.toLowerCase().split(".").pop();
    if (!["jpg", "jpeg", "png", "pdf"].includes(extension)) {
      Swal.fire("Unsupported file", "Choose a JPG, JPEG, PNG, or PDF bill.", "warning");
      return;
    }
    if (file.size > 12 * 1024 * 1024) {
      Swal.fire("File is too large", "The bill file must be 12 MB or smaller.", "warning");
      return;
    }
    setUploadFile(file);
  };

  const submitUpload = async (event) => {
    event.preventDefault();
    if (!uploadFile) {
      Swal.fire("Select a bill", "Choose the invoice image or PDF first.", "info");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", uploadFile);
      const response = await axiosInstance.post("/api/purchase/voucher/bill/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setShowUpload(false);
      setUploadFile(null);
      await loadBills({ quiet: true });
      const approved = response.data?.status === "APPROVED";
      await Swal.fire({
        icon: approved ? "success" : "warning",
        title: approved ? "Matched & approved" : "Review required",
        text: response.data?.message,
        confirmButtonColor: approved ? "#16a36a" : "#e26333",
      });
      if (response.data?.recordId) openDetails(response.data.recordId);
    } catch (error) {
      Swal.fire("Upload stopped", errorMessage(error, "The bill could not be processed."), "error");
    } finally {
      setUploading(false);
    }
  };

  const openDetails = async (id) => {
    setShowDetails(true);
    setDetailsLoading(true);
    setEditing(false);
    setSelected(null);
    try {
      const response = await axiosInstance.get(`/api/purchase/voucher/bill/details/${id}`);
      setSelected(response.data?.data || null);
    } catch (error) {
      setShowDetails(false);
      Swal.fire("Unable to open bill", errorMessage(error, "Bill details could not be loaded."), "error");
    } finally {
      setDetailsLoading(false);
    }
  };

  const refreshSelected = async (id) => {
    const response = await axiosInstance.get(`/api/purchase/voucher/bill/details/${id}`);
    setSelected(response.data?.data || null);
  };

  const startEditing = () => {
    if (!selected || selected.Status === "APPROVED") return;
    setEditForm({
      voucherNumber: selected.VoucherNumber || "",
    });
    setCorrectionLookup(null);
    setEditing(true);
  };

  const fetchCorrectionVoucher = async () => {
    const voucherNumber = editForm.voucherNumber.trim();
    if (!voucherNumber) return;

    setCorrectionLookupLoading(true);
    setCorrectionLookup(null);
    try {
      const response = await axiosInstance.post("/api/purchase/voucher/expense-lookup", { voucherNumber });
      setCorrectionLookup(response.data);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Lookup failed",
        text: errorMessage(error, "Voucher details could not be fetched."),
        customClass: VOUCHER_SWAL_LAYER,
      });
    } finally {
      setCorrectionLookupLoading(false);
    }
  };

  const submitCorrection = async (event) => {
    event.preventDefault();
    if (!selected) return;
    if (!correctionLookup?.found) {
      Swal.fire({
        icon: "info",
        title: "Fetch Voucher Details",
        text: "Fetch a valid ExpenseEntries Voucher Number before confirming.",
        customClass: VOUCHER_SWAL_LAYER,
      });
      return;
    }

    if (!correctionCanConfirm) {
      Swal.fire({
        icon: "warning",
        title: "Unique Voucher required",
        text: "This Voucher resolves to multiple ExpenseEntries rows and cannot be accepted or locked.",
        customClass: VOUCHER_SWAL_LAYER,
      });
      return;
    }

    const expenseAmounts = [...new Set(correctionMatches.map((entry) => money(entry.amount)))].join(", ");
    const confirmation = await Swal.fire({
      icon: correctionHasExactMatch ? "question" : "warning",
      title: correctionHasExactMatch ? "Confirm Voucher match?" : "Accept amount mismatch and lock?",
      text: correctionHasExactMatch
        ? `Voucher ${correctionLookup.voucherNumber} and amount ${money(selected.OCRAmount)} match ExpenseEntries. Confirming will approve and lock this bill.`
        : `Voucher ${correctionLookup.voucherNumber} exists, but bill amount ${money(selected.OCRAmount)} differs from ExpenseEntries (${expenseAmounts || "invalid amount"}). Your manual acceptance will approve and permanently lock this bill.`,
      showCancelButton: true,
      cancelButtonText: "Go back",
      confirmButtonText: correctionHasExactMatch ? "Yes, confirm & match" : "Yes, accept & lock",
      confirmButtonColor: correctionHasExactMatch ? "#45a36d" : "#dc8b32",
      reverseButtons: true,
      customClass: VOUCHER_SWAL_LAYER,
    });
    if (!confirmation.isConfirmed) return;

    setSaving(true);
    try {
      const response = await axiosInstance.put(`/api/purchase/voucher/bill/update/${selected.ID}`, {
        voucherNumber: editForm.voucherNumber,
        confirmVoucherDetails: true,
        acceptAmountMismatch: correctionIsManualOverride,
      });
      await Promise.all([refreshSelected(selected.ID), loadBills({ quiet: true })]);
      setEditing(false);
      Swal.fire({
        icon: response.data?.status === "APPROVED" ? "success" : "warning",
        title: response.data?.status === "APPROVED" ? "Matched & approved" : "Saved for review",
        text: response.data?.message,
        confirmButtonColor: "#5b52e8",
        customClass: VOUCHER_SWAL_LAYER,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Update stopped",
        text: errorMessage(error, "The correction could not be saved."),
        customClass: VOUCHER_SWAL_LAYER,
      });
    } finally {
      setSaving(false);
    }
  };

  const exportVoucherBills = async (format) => {
    const isTally = format === "tally";
    const endpoint = isTally
      ? "/api/purchase/voucher/bill/export/tally"
      : "/api/purchase/voucher/bill/export";
    const query = selectedExportIds.length > 0 ? `?ids=${selectedExportIds.join(",")}` : "";

    setExportingFormat(format);
    try {
      const response = await axiosInstance.get(`${endpoint}${query}`, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = isTally
        ? `Voucher_Bills_Tally_${new Date().toISOString().slice(0, 10)}.xlsx`
        : `Approved_Voucher_Bills_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      let message = errorMessage(error, "The Voucher export could not be downloaded.");
      const responseData = error?.response?.data;
      if (typeof Blob !== "undefined" && responseData instanceof Blob) {
        try {
          const payload = JSON.parse(await responseData.text());
          message = payload.message || message;
        } catch (_parseError) { /* keep the normal error message */ }
      }
      Swal.fire("Export stopped", message, "error");
    } finally {
      setExportingFormat(null);
    }
  };

  const deleteBill = async (record) => {
    if (!record || record.Status === "APPROVED") return;
    const confirmation = await Swal.fire({
      icon: "warning",
      title: "Delete this uploaded bill?",
      text: "The OCR record and stored bill file will be removed.",
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#dc3f4f",
    });
    if (!confirmation.isConfirmed) return;

    setActionId(record.ID);
    try {
      await axiosInstance.delete(`/api/purchase/voucher/bill/delete/${record.ID}`);
      if (selected?.ID === record.ID) setShowDetails(false);
      await loadBills({ quiet: true });
      Swal.fire("Deleted", "The Voucher bill was removed.", "success");
    } catch (error) {
      Swal.fire("Delete stopped", errorMessage(error, "The bill could not be deleted."), "error");
    } finally {
      setActionId(null);
    }
  };

  const viewFile = async (record) => {
    if (!record) return;
    setActionId(record.ID);
    try {
      const response = await axiosInstance.get(`/api/purchase/voucher/bill/file/${record.ID}`, {
        responseType: "blob",
      });
      const objectUrl = URL.createObjectURL(response.data);
      const popup = window.open(objectUrl, "_blank", "noopener,noreferrer");
      if (!popup) {
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = record.OriginalFileName || `voucher-bill-${record.ID}`;
        link.click();
      }
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
    } catch (error) {
      Swal.fire("File unavailable", errorMessage(error, "The stored bill could not be opened."), "error");
    } finally {
      setActionId(null);
    }
  };

  const openVoucherLookup = () => {
    setLookupVoucher("");
    setLookupResult(null);
    setShowLookup(true);
  };

  const searchVoucher = async (event) => {
    event.preventDefault();
    const voucherNumber = lookupVoucher.trim();
    if (!voucherNumber) return;

    setLookupLoading(true);
    setLookupResult(null);
    try {
      const response = await axiosInstance.post("/api/purchase/voucher/expense-lookup", { voucherNumber });
      setLookupResult(response.data);
    } catch (error) {
      Swal.fire("Search failed", errorMessage(error, "ExpenseEntries could not be searched."), "error");
    } finally {
      setLookupLoading(false);
    }
  };

  const openPromptSettings = async () => {
    setShowPrompt(true);
    setPromptLoading(true);
    try {
      const response = await axiosInstance.get("/api/purchase/voucher/settings/prompt");
      setPromptText(response.data?.prompt || "");
    } catch (error) {
      setShowPrompt(false);
      Swal.fire("Prompt unavailable", errorMessage(error, "The Voucher OCR prompt could not be loaded."), "error");
    } finally {
      setPromptLoading(false);
    }
  };

  const savePrompt = async () => {
    setPromptSaving(true);
    try {
      const response = await axiosInstance.put("/api/purchase/voucher/settings/prompt", { prompt: promptText });
      setShowPrompt(false);
      Swal.fire("Saved", response.data?.message, "success");
    } catch (error) {
      Swal.fire("Prompt not saved", errorMessage(error, "Please review the prompt and try again."), "error");
    } finally {
      setPromptSaving(false);
    }
  };

  const columns = [
    {
      name: "Bill / Vendor",
      minWidth: "220px",
      grow: 1.5,
      cell: (row) => (
        <button type="button" className="voucher-table-link" onClick={() => openDetails(row.ID)}>
          <strong>{row.PartyName || "Vendor not detected"}</strong>
          <span>Invoice: {row.InvoiceNumber || "Not detected"}</span>
          <small>Record #{row.ID}</small>
        </button>
      ),
    },
    {
      name: "Voucher Number",
      minWidth: "150px",
      cell: (row) => (
        <div className="voucher-table-vn">
          <strong>{row.VoucherNumber || "Missing"}</strong>
          <span>{expenseMatchLabel(row)}</span>
        </div>
      ),
    },
    {
      name: "Amount Check",
      minWidth: "190px",
      cell: (row) => (
        <div className="voucher-amount-pair">
          <span><small>Bill</small>{money(row.OCRAmount)}</span>
          <span><small>Expense</small>{money(row.ExpenseAmount)}</span>
        </div>
      ),
    },
    {
      name: "Status",
      minWidth: "145px",
      cell: (row) => (
        <div className="voucher-table-status">
          <StatusBadge status={row.Status} />
          {row.MismatchReasons?.[0] && <small title={row.MismatchReasons.join(" ")}>{row.MismatchReasons[0]}</small>}
        </div>
      ),
    },
    {
      name: "Bill Date",
      width: "115px",
      selector: (row) => row.BillDate,
      cell: (row) => <span className="voucher-table-date">{dateOnly(row.BillDate)}</span>,
    },
    {
      name: "Actions",
      width: "170px",
      right: true,
      cell: (row) => (
        <div className="voucher-row-actions">
          <button type="button" title="Open uploaded bill" onClick={() => viewFile(row)} disabled={actionId === row.ID}><Eye size={16} /></button>
          <button type="button" title="Open details" onClick={() => openDetails(row.ID)}><FileText size={16} /></button>
          {row.Status !== "APPROVED" && (
            <button type="button" className="is-delete" title="Delete" onClick={() => deleteBill(row)} disabled={actionId === row.ID}><Trash2 size={16} /></button>
          )}
          {row.Status === "APPROVED" && <span className="voucher-locked-label"><LockKeyhole size={13} /> Locked</span>}
        </div>
      ),
    },
  ];

  const tableStyles = {
    headCells: { style: { background: "#f8fafc", color: "#667085", fontSize: "10px", fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", borderBottom: "1px solid #dfe5ef" } },
    rows: { style: { minHeight: "76px", borderBottom: "1px solid #edf1f6" } },
    cells: { style: { paddingTop: "9px", paddingBottom: "9px" } },
  };

  return (
    <div className="d-flex dashboard bg-light voucher-dashboard">
      <div className={isSidebarVisible ? "sidebar-container" : "sidebar-hidden"}><Sidebar /></div>
      <div className="flex-grow-1 voucher-dashboard__content">
        <Header toggleSidebar={() => setIsSidebarVisible((visible) => !visible)} isSidebarVisible={isSidebarVisible} />

        <main className="main-container p-4 text-start voucher-main">
          <PurchaseValidationModeSwitch />

          <header className="voucher-hero">
            <div className="voucher-hero__copy">
              <span className="voucher-eyebrow"><ReceiptIndianRupee size={15} /> VoucherMatch</span>
              <h1>Voucher Bill Verification</h1>
              <p>Match the handwritten <b>VN / Voucher Number</b> and final bill amount with ExpenseEntries.</p>
            </div>
            <div className="voucher-hero__actions">
              <button type="button" className="voucher-button voucher-button--soft" onClick={openVoucherLookup}>
                <Search size={16} /> Voucher Lookup
              </button>
              {isAdmin && (
                <button type="button" className="voucher-button voucher-button--soft" onClick={openPromptSettings}>
                  <Settings2 size={16} /> OCR Prompt
                </button>
              )}
              <button type="button" className="voucher-button voucher-button--primary" onClick={() => setShowUpload(true)}>
                <UploadCloud size={17} /> Upload Voucher Bill
              </button>
            </div>
          </header>

          <section className="voucher-understand" aria-label="How Voucher validation works">
            <div className="voucher-understand__title">
              <Info size={18} />
              <div><strong>What this page checks</strong><span>Exact VN + amount matches are approved and locked automatically.</span></div>
            </div>
            <div className="voucher-understand__steps">
              <span><b>1</b> Read handwritten VN</span>
              <i>→</i>
              <span><b>2</b> Read final grand total</span>
              <i>→</i>
              <span><b>3</b> Match ExpenseEntries</span>
              <i>→</i>
              <span><b>4</b> Auto approve & lock</span>
            </div>
          </section>

          <section className="voucher-metrics">
            <MetricCard label="Total Bills" value={stats.Total || 0} detail="All Voucher uploads" tone="neutral" icon={FileText} />
            <MetricCard label="Not Verified" value={stats.NotVerified || 0} detail="Needs correction" tone="danger" icon={AlertCircle} />
            <MetricCard label="Approved" value={stats.Approved || 0} detail="Auto-matched and locked" tone="primary" icon={ShieldCheck} />
          </section>

          <section className="voucher-status-guide">
            {["RED", "APPROVED"].map((status) => {
              const meta = STATUS_META[status];
              const Icon = meta.icon;
              return (
                <article key={status} className={`voucher-guide voucher-guide--${status.toLowerCase()}`}>
                  <Icon size={18} />
                  <div><strong>{meta.label}</strong><span>{meta.helper}</span></div>
                </article>
              );
            })}
          </section>

          <section className="voucher-list-card">
            <div className="voucher-list-toolbar">
              <div className="voucher-filter-tabs" role="tablist" aria-label="Voucher bill status">
                {[
                  ["ALL", "All", stats.Total],
                  ["RED", "Not Verified", stats.NotVerified],
                  ["APPROVED", "Approved", stats.Approved],
                ].map(([id, label, count]) => (
                  <button key={id} type="button" className={activeStatus === id ? "is-active" : ""} onClick={() => setActiveStatus(id)}>
                    {label} <span>{count || 0}</span>
                  </button>
                ))}
              </div>
              <div className="voucher-list-tools">
                <label className="voucher-search">
                  <Search size={15} />
                  <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search VN, vendor or invoice…" />
                </label>
                <button type="button" className="voucher-export-button voucher-export-button--excel" onClick={() => exportVoucherBills("excel")} disabled={Boolean(exportingFormat) || !(stats.Approved > 0)}>
                  {exportingFormat === "excel" ? <LoaderCircle size={15} className="is-spinning" /> : <DownloadCloud size={15} />}
                  Excel{selectedExportIds.length > 0 ? ` (${selectedExportIds.length})` : ""}
                </button>
                <button type="button" className="voucher-export-button voucher-export-button--tally" onClick={() => exportVoucherBills("tally")} disabled={Boolean(exportingFormat) || !(stats.Approved > 0)}>
                  {exportingFormat === "tally" ? <LoaderCircle size={15} className="is-spinning" /> : <DownloadCloud size={15} />}
                  Tally Format{selectedExportIds.length > 0 ? ` (${selectedExportIds.length})` : ""}
                </button>
                <button type="button" className="voucher-icon-button" title="Refresh list" onClick={() => loadBills()} disabled={loading}>
                  <RefreshCw size={17} className={loading ? "is-spinning" : ""} />
                </button>
              </div>
            </div>

            {loadError && (
              <div className="voucher-load-error"><AlertCircle size={17} /> {loadError}<button type="button" onClick={() => loadBills()}>Try again</button></div>
            )}

            <DataTable
              columns={columns}
              data={filteredRows}
              progressPending={loading}
              progressComponent={<div className="voucher-table-message"><LoaderCircle className="is-spinning" size={22} /> Loading Voucher bills…</div>}
              noDataComponent={<div className="voucher-table-message"><ReceiptIndianRupee size={28} /><strong>No Voucher bills found</strong><span>Upload a bill or change the current filter.</span></div>}
              pagination
              paginationPerPage={10}
              paginationRowsPerPageOptions={[10, 20, 50]}
              highlightOnHover
              selectableRows
              selectableRowDisabled={(row) => row.Status !== "APPROVED"}
              selectableRowsHighlight
              onSelectedRowsChange={({ selectedRows }) => setSelectedExportRows(selectedRows)}
              responsive
              customStyles={tableStyles}
            />
          </section>
        </main>
      </div>

      {showLookup && (
        <div className="voucher-modal-backdrop" role="presentation" onMouseDown={() => !lookupLoading && setShowLookup(false)}>
          <section className="voucher-modal voucher-lookup-modal" role="dialog" aria-modal="true" aria-labelledby="voucher-lookup-title" onMouseDown={(event) => event.stopPropagation()}>
            <header className="voucher-modal__header">
              <div>
                <span className="voucher-modal__icon"><Database size={20} /></span>
                <div>
                  <h2 id="voucher-lookup-title">Voucher Number Lookup</h2>
                  <p>Check whether a Voucher Number exists in ExpenseEntries before uploading a bill.</p>
                </div>
              </div>
              <button type="button" onClick={() => setShowLookup(false)} disabled={lookupLoading}><X size={20} /></button>
            </header>

            <form onSubmit={searchVoucher}>
              <div className="voucher-modal__body">
                <div className="voucher-lookup-search">
                  <label htmlFor="voucher-lookup-input">Voucher Number</label>
                  <div>
                    <span><Search size={18} /></span>
                    <input
                      id="voucher-lookup-input"
                      value={lookupVoucher}
                      onChange={(event) => {
                        setLookupVoucher(event.target.value);
                        setLookupResult(null);
                      }}
                      placeholder="Example: 3416 or VN: 3416"
                      maxLength={100}
                      autoFocus
                      required
                    />
                    <button type="submit" className="voucher-button voucher-button--primary" disabled={lookupLoading || !lookupVoucher.trim()}>
                      {lookupLoading ? <><LoaderCircle size={16} className="is-spinning" /> Searching…</> : "Search"}
                    </button>
                  </div>
                  <small>Exact Voucher Number search · Read-only lookup from dbo.ExpenseEntries</small>
                </div>

                {lookupResult && (
                  <div className={`voucher-lookup-result ${lookupResult.found ? "is-found" : "is-missing"}`}>
                    <div className="voucher-lookup-summary">
                      {lookupResult.found ? <CheckCircle2 size={22} /> : <AlertCircle size={22} />}
                      <div>
                        <strong>{lookupResult.found ? "Voucher found" : "Voucher not found"}</strong>
                        <span>{lookupResult.message}</span>
                      </div>
                    </div>

                    {lookupResult.found && lookupResult.matches?.map((entry) => (
                      <article className="voucher-lookup-entry" key={entry.id}>
                        <div className="voucher-lookup-entry__heading">
                          <span>Expense Entry #{entry.id}</span>
                          <strong>{money(entry.amount)}</strong>
                        </div>
                        <div className="voucher-detail-grid">
                          <Field label="Voucher Number" value={entry.voucherNumber} accent mono />
                          <Field label="Amount" value={entry.amount === null ? `Invalid value: ${entry.amountSource || "—"}` : money(entry.amount)} accent />
                          <Field label="Ledger" value={entry.ledgerName} />
                          <Field label="Ledger Code" value={entry.ledgerCode} mono />
                          <Field label="Expense Date" value={dateOnly(entry.expenseDate)} />
                          <Field label="Budget Head" value={entry.budgetHead} />
                          <Field label="Employee" value={entry.employeeName} />
                          <Field label="Details" value={entry.details} />
                        </div>
                      </article>
                    ))}
                  </div>
                )}
              </div>
              <footer className="voucher-modal__footer">
                <button type="button" className="voucher-button voucher-button--soft" onClick={() => setShowLookup(false)} disabled={lookupLoading}>Close</button>
              </footer>
            </form>
          </section>
        </div>
      )}

      {showUpload && (
        <div className="voucher-modal-backdrop" role="presentation" onMouseDown={() => !uploading && setShowUpload(false)}>
          <section className="voucher-modal voucher-upload-modal" role="dialog" aria-modal="true" aria-labelledby="voucher-upload-title" onMouseDown={(event) => event.stopPropagation()}>
            <header className="voucher-modal__header">
              <div><span className="voucher-modal__icon"><UploadCloud size={20} /></span><div><h2 id="voucher-upload-title">Upload Voucher Bill</h2><p>AI will read handwritten VN and the final payable amount.</p></div></div>
              <button type="button" onClick={() => !uploading && setShowUpload(false)} disabled={uploading}><X size={20} /></button>
            </header>
            <form onSubmit={submitUpload}>
              <div className="voucher-modal__body">
                <div className="voucher-upload-rules">
                  <span><Hash size={16} /><b>Voucher:</b> handwritten VN / Voucher No.</span>
                  <span><ReceiptIndianRupee size={16} /><b>Amount:</b> final grand total including tax</span>
                  <span><Info size={16} /><b>Not used:</b> printed Invoice No. or Buyer Order No.</span>
                </div>

                <input
                  ref={fileInputRef}
                  className="voucher-file-input"
                  type="file"
                  accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                  onChange={(event) => chooseFile(event.target.files?.[0])}
                />
                <button
                  type="button"
                  className={`voucher-dropzone ${uploadFile ? "has-file" : ""}`}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={(event) => { event.preventDefault(); chooseFile(event.dataTransfer.files?.[0]); }}
                  disabled={uploading}
                >
                  {uploadFile ? <FileCheck2 size={34} /> : <UploadCloud size={34} />}
                  <strong>{uploadFile ? uploadFile.name : "Drop bill here or click to browse"}</strong>
                  <span>{uploadFile ? `${(uploadFile.size / 1024 / 1024).toFixed(2)} MB · Click to replace` : "JPG, PNG or PDF · Maximum 12 MB"}</span>
                </button>

                <div className="voucher-upload-note"><Info size={16} /><span>Use a clear, straight image where both the handwritten voucher number and final total are readable. For PDFs, the first page is checked.</span></div>
              </div>
              <footer className="voucher-modal__footer">
                <button type="button" className="voucher-button voucher-button--soft" onClick={() => setShowUpload(false)} disabled={uploading}>Cancel</button>
                <button type="submit" className="voucher-button voucher-button--primary" disabled={!uploadFile || uploading}>
                  {uploading ? <><LoaderCircle size={17} className="is-spinning" /> Reading & matching…</> : <><UploadCloud size={17} /> Upload and verify</>}
                </button>
              </footer>
            </form>
          </section>
        </div>
      )}

      {showDetails && (
        <div className="voucher-modal-backdrop" role="presentation" onMouseDown={() => !saving && setShowDetails(false)}>
          <section className="voucher-modal voucher-details-modal" role="dialog" aria-modal="true" aria-labelledby="voucher-details-title" onMouseDown={(event) => event.stopPropagation()}>
            <header className="voucher-modal__header">
              <div><span className="voucher-modal__icon"><ReceiptIndianRupee size={20} /></span><div><h2 id="voucher-details-title">Voucher bill details</h2><p>{selected ? `Record #${selected.ID} · ${selected.OriginalFileName}` : "Loading record…"}</p></div></div>
              <button type="button" onClick={() => setShowDetails(false)} disabled={saving}><X size={20} /></button>
            </header>

            {detailsLoading || !selected ? (
              <div className="voucher-detail-loading"><LoaderCircle className="is-spinning" size={25} /> Loading verified data…</div>
            ) : editing ? (
              <form onSubmit={submitCorrection}>
                <div className="voucher-modal__body">
                  <div className="voucher-edit-warning"><Info size={17} /><span>Invoice fields come from OCR and cannot be changed here. Correct only the handwritten Voucher Number, fetch its ExpenseEntries details, then save.</span></div>
                  <div className="voucher-edit-grid">
                    <label className="is-readonly"><span>Vendor / Party · OCR</span><input value={selected.PartyName || ""} readOnly /></label>
                    <label className="is-readonly"><span>Printed Invoice No. · OCR</span><input value={selected.InvoiceNumber || ""} readOnly /></label>
                    <label className="is-readonly"><span>Bill Date · OCR</span><input value={dateOnly(selected.BillDate)} readOnly /></label>
                    <label className="is-readonly"><span>Final Bill Amount · OCR</span><input value={money(selected.OCRAmount)} readOnly /></label>
                    <label className="is-important voucher-edit-vn">
                      <span>Handwritten Voucher No. (VN) · Editable</span>
                      <div className="voucher-edit-vn__controls">
                        <input
                          value={editForm.voucherNumber}
                          onChange={(event) => {
                            setEditForm({ voucherNumber: event.target.value });
                            setCorrectionLookup(null);
                          }}
                          maxLength={100}
                          autoFocus
                          required
                        />
                        <button type="button" className="voucher-button voucher-button--soft" onClick={fetchCorrectionVoucher} disabled={correctionLookupLoading || !editForm.voucherNumber.trim()}>
                          {correctionLookupLoading ? <><LoaderCircle size={16} className="is-spinning" /> Fetching…</> : <><Database size={16} /> Fetch Voucher Details</>}
                        </button>
                      </div>
                    </label>
                  </div>

                  {correctionLookup && (
                    <div className={`voucher-correction-result ${correctionLookup.found ? "is-found" : "is-missing"}`}>
                      <div className="voucher-correction-result__summary">
                        {correctionLookup.found ? <CheckCircle2 size={19} /> : <AlertCircle size={19} />}
                        <div><strong>{correctionLookup.found ? "Voucher found in ExpenseEntries" : "Voucher not found"}</strong><span>{correctionLookup.message}</span></div>
                      </div>
                      {correctionLookup.found && correctionLookup.matches?.map((entry) => (
                        <div className="voucher-correction-result__entry" key={entry.id}>
                          <Field label="Expense Entry" value={`#${entry.id}`} mono />
                          <Field label="Voucher No." value={entry.voucherNumber} accent mono />
                          <Field label="Expense Amount" value={money(entry.amount)} accent />
                          <Field label="Fixed Bill Amount" value={money(selected.OCRAmount)} />
                          <Field label="Ledger" value={entry.ledgerName} />
                          <Field label="Expense Date" value={dateOnly(entry.expenseDate)} />
                          <div className={`voucher-correction-amount ${sameMoney(entry.amount, selected.OCRAmount) ? "is-match" : "is-mismatch"}`}>
                            {sameMoney(entry.amount, selected.OCRAmount) ? <CheckCircle2 size={15} /> : <AlertCircle size={15} />}
                            {sameMoney(entry.amount, selected.OCRAmount) ? "VN and amount match" : "VN found · amount mismatch"}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <footer className="voucher-modal__footer">
                  <button type="button" className="voucher-button voucher-button--soft" onClick={() => setEditing(false)} disabled={saving}>Cancel</button>
                  {correctionLookup?.found ? (
                    <button type="submit" className="voucher-button voucher-button--success" disabled={saving || correctionLookupLoading || !correctionCanConfirm}>
                      {saving
                        ? <><LoaderCircle size={17} className="is-spinning" /> Locking…</>
                        : <><ShieldCheck size={17} /> {correctionIsManualOverride ? "Accept & Lock" : correctionCanConfirm ? "Confirm & Match" : "Multiple matches"}</>}
                    </button>
                  ) : (
                    <span className="voucher-confirm-hint">Fetch Voucher Details to continue</span>
                  )}
                </footer>
              </form>
            ) : (
              <>
                <div className="voucher-modal__body voucher-details-body">
                  <div className={`voucher-result-banner voucher-result-banner--${selected.Status.toLowerCase()}`}>
                    <StatusBadge status={selected.Status} />
                    <div><strong>{selectedReview.title}</strong><span>{selectedReview.detail}</span></div>
                  </div>

                  {selected.MismatchReasons?.length > 0 && (
                    <div className="voucher-reasons"><strong><AlertCircle size={16} /> Why it is not verified</strong><ul>{selected.MismatchReasons.map((reason, index) => <li key={`${reason}-${index}`}>{reason}</li>)}</ul></div>
                  )}

                  <div className="voucher-detail-columns">
                    <section>
                      <h3><FileText size={17} /> Bill OCR</h3>
                      <div className="voucher-detail-grid">
                        <Field label="Vendor / Party" value={selected.PartyName} />
                        <Field label="Printed Invoice No." value={selected.InvoiceNumber} mono />
                        <Field label="Bill Date" value={dateOnly(selected.BillDate)} />
                        <Field label="Handwritten Voucher No." value={selected.VoucherNumber} accent mono />
                        <Field label="Final Bill Amount" value={money(selected.OCRAmount)} accent />
                        <Field label="OCR Evidence" value={selected.ExtractedData?.voucherEvidence} />
                      </div>
                    </section>
                    <section>
                      <h3><Database size={17} /> ExpenseEntries comparison</h3>
                      {selectedExpense ? (
                        <>
                          <div className={`voucher-match-note ${selectedReview.kind === "AMOUNT_MISMATCH" ? "is-warning" : "is-success"}`}>
                            <CheckCircle2 size={16} />
                            <div>
                              <strong>Voucher number matched</strong>
                              <span>{selectedReview.kind === "AMOUNT_MISMATCH" ? "Only the amount is different." : "ExpenseEntries record linked successfully."}</span>
                            </div>
                          </div>
                          <div className="voucher-detail-grid">
                            <Field label="Expense Entry" value={`#${selectedExpense.id}`} mono />
                            <Field label="Ledger" value={selectedExpense.ledgerName} />
                            <Field label="Voucher No." value={selectedExpense.voucherNumber || selected.VoucherNumber} accent mono />
                            <Field label="Expense Amount" value={money(selectedExpense.amount)} accent />
                            <Field label="Bill Amount" value={money(selected.OCRAmount)} />
                            {selectedAmountDifference !== null && selectedAmountDifference > 0 && <Field label="Amount Difference" value={money(selectedAmountDifference)} accent />}
                            <Field label="Expense Date" value={dateOnly(selectedExpense.expenseDate)} />
                            <Field label="Budget Head" value={selectedExpense.budgetHead} />
                          </div>
                        </>
                      ) : (
                        <div className="voucher-no-match">
                          <Database size={25} />
                          <strong>{selectedReview.kind === "VOUCHER_MISSING" ? "Voucher number was not detected" : selectedReview.kind === "VOUCHER_NOT_FOUND" ? "Voucher number not found in ExpenseEntries" : "No unique ExpenseEntries match"}</strong>
                          <span>{selectedReview.kind === "VOUCHER_MISSING" ? "Enter the handwritten VN from the bill." : selectedReview.kind === "VOUCHER_NOT_FOUND" ? "Check the handwritten VN or create the ExpenseEntries record first." : "Review the specific verification reason above."}</span>
                        </div>
                      )}
                    </section>
                  </div>

                  <div className="voucher-audit-row">
                    <span>Uploaded by <b>{selected.CreatedBy || "—"}</b> on {dateTime(selected.CreatedAt)}</span>
                    {selected.ApprovedAt && <span>Approved by <b>{selected.ApprovedBy || "—"}</b> on {dateTime(selected.ApprovedAt)}</span>}
                  </div>
                </div>
                <footer className="voucher-modal__footer voucher-detail-actions">
                  <button type="button" className="voucher-button voucher-button--soft" onClick={() => viewFile(selected)} disabled={actionId === selected.ID}><Eye size={16} /> Open bill</button>
                  <div>
                    {selected.Status !== "APPROVED" && <button type="button" className="voucher-button voucher-button--soft" onClick={startEditing}><Pencil size={16} /> Correct values</button>}
                  </div>
                </footer>
              </>
            )}
          </section>
        </div>
      )}

      {showPrompt && (
        <div className="voucher-modal-backdrop" role="presentation" onMouseDown={() => !promptSaving && setShowPrompt(false)}>
          <section className="voucher-modal voucher-prompt-modal" role="dialog" aria-modal="true" aria-labelledby="voucher-prompt-title" onMouseDown={(event) => event.stopPropagation()}>
            <header className="voucher-modal__header">
              <div><span className="voucher-modal__icon"><Settings2 size={20} /></span><div><h2 id="voucher-prompt-title">Voucher OCR Prompt</h2><p>Admin-only instructions used for VN and total extraction.</p></div></div>
              <button type="button" onClick={() => setShowPrompt(false)} disabled={promptSaving}><X size={20} /></button>
            </header>
            <div className="voucher-modal__body">
              {promptLoading ? <div className="voucher-detail-loading"><LoaderCircle className="is-spinning" size={22} /> Loading prompt…</div> : <textarea className="voucher-prompt-textarea" value={promptText} onChange={(event) => setPromptText(event.target.value)} />}
            </div>
            <footer className="voucher-modal__footer">
              <button type="button" className="voucher-button voucher-button--soft" onClick={() => setShowPrompt(false)} disabled={promptSaving}>Cancel</button>
              <button type="button" className="voucher-button voucher-button--primary" onClick={savePrompt} disabled={promptLoading || promptSaving || promptText.trim().length < 100}>{promptSaving ? <><LoaderCircle className="is-spinning" size={17} /> Saving…</> : <><Save size={17} /> Save prompt</>}</button>
            </footer>
          </section>
        </div>
      )}
    </div>
  );
}
